import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

export default function BuildsList() {
  const [builds, setBuilds] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchBuilds()
  }, [])

  const fetchBuilds = () => {
    setLoading(true)
    fetch('/api/builds')
      .then(response => response.json())
      .then(data => {
        setBuilds(data)
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching builds:', err)
        setLoading(false)
      })
  }

  const deleteBuild = (id) => {
    if (!confirm('Are you sure you want to delete this build? This will restore the parts to inventory.')) return

    fetch(`/api/builds/${id}`, { method: 'DELETE' })
      .then(response => {
        if (response.ok) {
          setBuilds(builds.filter(b => b.id !== id))
        } else {
          return response.json().then(data => {
            alert(data.errors?.join(', ') || 'Failed to delete build')
          })
        }
      })
      .catch(err => console.error('Error deleting build:', err))
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Build History</h1>
          <p className="mt-2 text-sm text-gray-600">
            View and manage completed builds
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/completebuild"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
          >
            + Complete a Build
          </Link>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading builds...</div>
          </div>
        ) : builds.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <p className="text-gray-500 mb-4">No builds recorded yet</p>
              <Link
                to="/completebuild"
                className="text-green-600 hover:text-green-700 font-medium"
              >
                Complete your first build &rarr;
              </Link>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Configuration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Options
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Van
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {builds.map((build) => (
                  <tr key={build.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(build.completed_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {build.configuration_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {build.option_names && build.option_names.length > 0
                        ? build.option_names.join(', ')
                        : '-'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {build.van_serial_number || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(build.total_cost)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <Link
                        to={`/builds/${build.id}`}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        View
                      </Link>
                      <button
                        onClick={() => deleteBuild(build.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 text-sm text-gray-600">
        Showing {builds.length} build{builds.length !== 1 ? 's' : ''}
      </div>
    </div>
  )
}
